import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import {
    fetchWorkerCandidatePublicationStatus,
    retryWorkerCandidatePublication,
    WorkerCandidatePublicationStatus,
} from '../../api/workersApi';
import { extractErrorMessageSync } from '../../utils/errorHandler';
import {
    COMMAND_STATUS_POLL_INITIAL_DELAY_MS,
    COMMAND_STATUS_POLL_MAX_TRANSIENT_FAILURES,
    commandStatusPollDelay,
    commandWasDefinitivelyRejected,
} from './feedbackCommandKey';

export function CandidatePublicationStatusAlert({
    workerId,
    canManageWorkers,
}: {
    workerId: string;
    canManageWorkers: boolean;
}) {
    const [status, setStatus] = useState<WorkerCandidatePublicationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(false);
    const [pollEnabled, setPollEnabled] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const generation = useRef(0);
    const activeWorkerId = useRef(workerId);
    activeWorkerId.current = workerId;

    const load = useCallback(async () => {
        if (!canManageWorkers || activeWorkerId.current !== workerId) return;
        const requestGeneration = ++generation.current;
        setLoading(true);
        setError(null);
        try {
            const result = await fetchWorkerCandidatePublicationStatus(workerId);
            if (requestGeneration !== generation.current || activeWorkerId.current !== workerId) return;
            setStatus(result);
            setPollEnabled(result?.state === 'PENDING' || result?.state === 'PROCESSING');
        } catch (loadError) {
            if (requestGeneration !== generation.current || activeWorkerId.current !== workerId) return;
            setStatus(null);
            setPollEnabled(false);
            setError(extractErrorMessageSync(loadError));
        } finally {
            if (requestGeneration === generation.current && activeWorkerId.current === workerId) {
                setLoading(false);
            }
        }
    }, [canManageWorkers, workerId]);

    useEffect(() => {
        generation.current += 1;
        setStatus(null);
        setError(null);
        setPollEnabled(false);
        setRetrying(false);
        void load();
        return () => {
            generation.current += 1;
        };
    }, [load]);

    useEffect(() => {
        if (!canManageWorkers || !pollEnabled || !status) return;
        if (status.state !== 'PENDING' && status.state !== 'PROCESSING') return;

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let transientFailures = 0;
        const targetWorkerId = workerId;
        const schedule = (delay: number) => {
            if (!cancelled) timer = setTimeout(() => void poll(), delay);
        };
        const poll = async () => {
            try {
                const result = await fetchWorkerCandidatePublicationStatus(targetWorkerId);
                if (cancelled || activeWorkerId.current !== targetWorkerId || !canManageWorkers) return;
                if (!result) {
                    setPollEnabled(false);
                    setError('Candidate publication status is no longer available. Refresh to reconcile the worker.');
                    return;
                }
                transientFailures = 0;
                setStatus(result);
                setError(null);
                if (result.state === 'SENT' || result.state === 'FAILED') {
                    setPollEnabled(false);
                    return;
                }
            } catch (pollError) {
                if (cancelled || activeWorkerId.current !== targetWorkerId || !canManageWorkers) return;
                if (commandWasDefinitivelyRejected(pollError)) {
                    setPollEnabled(false);
                    setError('Candidate publication status is no longer accessible. Refresh your permissions before retrying.');
                    return;
                }
                transientFailures += 1;
                if (transientFailures >= COMMAND_STATUS_POLL_MAX_TRANSIENT_FAILURES) {
                    setPollEnabled(false);
                    setError('Candidate publication status is temporarily unavailable. Refresh to reconcile the durable command.');
                    return;
                }
            }
            schedule(commandStatusPollDelay(transientFailures));
        };

        schedule(COMMAND_STATUS_POLL_INITIAL_DELAY_MS);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [canManageWorkers, pollEnabled, status, workerId]);

    const retry = async () => {
        if (!canManageWorkers || status?.state !== 'FAILED' || !status.retryable) return;
        const targetWorkerId = workerId;
        const requestGeneration = generation.current;
        setRetrying(true);
        setError(null);
        try {
            const result = await retryWorkerCandidatePublication(targetWorkerId);
            if (requestGeneration !== generation.current || activeWorkerId.current !== targetWorkerId) return;
            setStatus(result);
            setPollEnabled(result.state === 'PENDING' || result.state === 'PROCESSING');
        } catch (retryError) {
            if (requestGeneration === generation.current && activeWorkerId.current === targetWorkerId) {
                setError(extractErrorMessageSync(retryError));
            }
        } finally {
            if (requestGeneration === generation.current && activeWorkerId.current === targetWorkerId) {
                setRetrying(false);
            }
        }
    };

    if (!canManageWorkers) return null;
    if (loading) return <Alert severity="info" icon={<CircularProgress size={18} />}>Checking Saleslist publication status.</Alert>;
    if (error) {
        return (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => void load()}>Refresh</Button>}>
                {error}
            </Alert>
        );
    }
    if (!status) return null;
    if (status.state === 'SENT') return <Alert severity="success">Candidate is available in Saleslist.</Alert>;
    if (status.state === 'PENDING') return <Alert severity="info">Saleslist publication is queued for durable delivery.</Alert>;
    if (status.state === 'PROCESSING') return <Alert severity="info">Saleslist publication is being processed.</Alert>;

    return (
        <Alert
            severity="error"
            action={status.retryable ? (
                <Button color="inherit" size="small" disabled={retrying} onClick={() => void retry()}>
                    {retrying ? 'Retrying...' : 'Retry'}
                </Button>
            ) : undefined}
        >
            {status.retryable
                ? 'Saleslist publication failed. Retry the same durable command.'
                : 'Saleslist publication failed and cannot be retried automatically.'}
        </Alert>
    );
}
